/*
 * DSD Moderation Registry Foundation - p11dz150
 * Registry-only moderation operations contract. Defines report, flag, review,
 * escalation, queue, appeal handoff, and audit ownership for posts, comments,
 * reactions, profiles, stories, universes, galaxies, galaxy stories, galaxy chat,
 * and events. No Firebase reads/writes/realtime. No enforcement. No moderation actions executed.
 */
(function(){
  'use strict';
  if (window.DSDModerationRegistry && window.DSDModerationRegistry.version) return;

  var VERSION = 'p11dz150-moderation-registry-foundation-v1';
  var OWNER = 'ModerationRegistry';
  var FIREBASE_READS_ENABLED = false;
  var FIREBASE_WRITES_ENABLED = false;
  var REALTIME_ENABLED = false;
  var ENFORCEMENT_ENABLED = false;
  var ACTION_EXECUTION_ENABLED = false;

  function registry(){ return window.DSDRegistry || window.DSDRuntimeRegistry || null; }
  function claim(surface, meta){
    try {
      var r = registry();
      var payload = Object.assign({ version: VERSION }, meta || {});
      if (r && typeof r.claim === 'function') r.claim(surface, OWNER, payload);
      else if (r && typeof r.claimOwnership === 'function') r.claimOwnership(surface, OWNER, payload);
    } catch(e) {}
  }
  function event(name, meta){
    try {
      var r = registry();
      var payload = Object.assign({ version: VERSION, owner: OWNER }, meta || {});
      if (r && typeof r.event === 'function') r.event(name, payload);
      else if (r && typeof r.trackEvent === 'function') r.trackEvent(name, payload);
    } catch(e) {}
  }
  function clone(obj){ return JSON.parse(JSON.stringify(obj)); }
  function arrayify(v){ return Array.isArray(v) ? v : (v == null || v === '' ? [] : [v]); }
  function slug(v){ return String(v || '').trim().toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,''); }
  function unique(values){
    var seen = {};
    return arrayify(values).map(slug).filter(function(v){ if(!v || seen[v]) return false; seen[v]=true; return true; });
  }

  var doctrine = [
    'Every social surface must have a report path before launch.',
    'ModerationRegistry owns report, flag, queue, escalation, and review contracts only in this build.',
    'ModerationMetadata owns object-level ratings/states; ModerationRegistry owns operational review flow.',
    'GovernanceRegistry defines who may act; ModerationRegistry defines what action flow exists.',
    'TrustReputationRegistry may later inform prioritization but cannot override platform safety.',
    'Platform moderation always overrides universe, galaxy, event, story, and chat governance.',
    'No moderation action may mutate canonical state without future AuditLogRegistry coverage.',
    'No Firebase reads/writes/realtime are enabled in p11dz150.'
  ];

  var reportableEntityTypes = {
    user: { key:'user', owner:'IdentityRegistry', route:'profile' },
    profile: { key:'profile', owner:'ProfileRenderer', route:'profile' },
    post: { key:'post', owner:'CanonicalPostStore', route:'posts' },
    comment: { key:'comment', owner:'CommentRenderer', route:'comments' },
    reaction: { key:'reaction', owner:'ReactionRenderer', route:'reactions' },
    story: { key:'story', owner:'StoriesRenderer', route:'stories', future:true },
    universe: { key:'universe', owner:'GovernanceRegistry', route:'universes', future:true },
    galaxy: { key:'galaxy', owner:'GovernanceRegistry', route:'galaxies', future:true },
    galaxyStory: { key:'galaxyStory', owner:'StoriesRenderer', route:'galaxyStories', future:true },
    galaxyChat: { key:'galaxyChat', owner:'MessagingRenderer', route:'galaxyChat', future:true },
    event: { key:'event', owner:'GovernanceRegistry', route:'events', future:true },
    mediaAsset: { key:'mediaAsset', owner:'UploadRenderer', route:'media', future:true }
  };

  var reportReasons = {
    spam: { key:'spam', label:'Spam or automation', severity:'medium' },
    harassment: { key:'harassment', label:'Harassment or targeted abuse', severity:'high' },
    hateOrAbuse: { key:'hateOrAbuse', label:'Hate, abuse, or protected-class attack', severity:'high' },
    impersonation: { key:'impersonation', label:'Impersonation or identity misuse', severity:'high' },
    copyright: { key:'copyright', label:'Copyright or media ownership concern', severity:'medium' },
    consent: { key:'consent', label:'Consent or non-consensual content concern', severity:'critical' },
    illegalContent: { key:'illegalContent', label:'Illegal content concern', severity:'critical' },
    underageRisk: { key:'underageRisk', label:'Age or underage risk escalation', severity:'critical' },
    adultMisclassification: { key:'adultMisclassification', label:'Adult/sensitive content misclassified', severity:'high' },
    scamFraud: { key:'scamFraud', label:'Scam, solicitation, payment, or fraud concern', severity:'high' },
    privacy: { key:'privacy', label:'Privacy, doxxing, or personal information concern', severity:'critical' },
    violenceThreat: { key:'violenceThreat', label:'Violence, threat, or safety concern', severity:'critical' },
    selfHarm: { key:'selfHarm', label:'Self-harm or emergency safety concern', severity:'critical' },
    platformPolicy: { key:'platformPolicy', label:'General platform policy concern', severity:'medium' },
    other: { key:'other', label:'Other concern', severity:'low' }
  };

  var severityLevels = {
    low: { key:'low', rank:10, autoEscalate:false },
    medium: { key:'medium', rank:30, autoEscalate:false },
    high: { key:'high', rank:60, autoEscalate:true },
    critical: { key:'critical', rank:100, autoEscalate:true, platformReviewRequired:true }
  };

  var reviewStates = {
    received: { key:'received', terminal:false, label:'Received' },
    triaged: { key:'triaged', terminal:false, label:'Triaged' },
    queued: { key:'queued', terminal:false, label:'Queued' },
    underReview: { key:'underReview', terminal:false, label:'Under Review' },
    escalated: { key:'escalated', terminal:false, label:'Escalated' },
    actionRecommended: { key:'actionRecommended', terminal:false, label:'Action Recommended' },
    actioned: { key:'actioned', terminal:true, label:'Actioned' },
    dismissed: { key:'dismissed', terminal:true, label:'Dismissed' },
    appealed: { key:'appealed', terminal:false, label:'Appealed' },
    appealResolved: { key:'appealResolved', terminal:true, label:'Appeal Resolved' }
  };

  var queues = {
    intake: { key:'intake', owner:'platformModerator', purpose:'All new reports begin here.' },
    adultSafety: { key:'adultSafety', owner:'platformModerator', purpose:'Adult, sensitive, consent, age, and illegal-content risk review.' },
    universe: { key:'universe', owner:'universeModerator', purpose:'Universe-scoped reports that do not require platform escalation.' },
    galaxy: { key:'galaxy', owner:'galaxyModerator', purpose:'Galaxy-scoped reports that do not require universe/platform escalation.' },
    chat: { key:'chat', owner:'chatModerator', purpose:'Galaxy chat and event chat reports.' },
    appeals: { key:'appeals', owner:'platformAdmin', purpose:'Appeal review after moderation action.' },
    emergency: { key:'emergency', owner:'platformAdmin', purpose:'Critical safety and legal risk queue.' }
  };

  var recommendedActions = {
    noAction: { key:'noAction', auditRequired:true },
    labelContent: { key:'labelContent', auditRequired:true },
    hideContent: { key:'hideContent', auditRequired:true },
    lockContent: { key:'lockContent', auditRequired:true },
    removeContent: { key:'removeContent', auditRequired:true },
    warnUser: { key:'warnUser', auditRequired:true },
    muteUser: { key:'muteUser', auditRequired:true },
    suspendUserScoped: { key:'suspendUserScoped', auditRequired:true },
    suspendUserPlatform: { key:'suspendUserPlatform', auditRequired:true, platformOnly:true },
    freezeGalaxy: { key:'freezeGalaxy', auditRequired:true },
    freezeUniverse: { key:'freezeUniverse', auditRequired:true, platformOrUniverseAdmin:true },
    freezeStorySurface: { key:'freezeStorySurface', auditRequired:true },
    freezeChat: { key:'freezeChat', auditRequired:true },
    escalatePlatform: { key:'escalatePlatform', auditRequired:true, platformOnly:false },
    lawSafetyEscalation: { key:'lawSafetyEscalation', auditRequired:true, platformOnly:true }
  };

  var pathContracts = {
    reports: 'moderation/reports/{reportId}',
    entityReports: 'moderation/entities/{entityType}/{entityId}/reports/{reportId}',
    queues: 'moderation/queues/{queueKey}/items/{reportId}',
    reviewActions: 'moderation/reports/{reportId}/actions/{actionId}',
    appeals: 'moderation/appeals/{appealId}',
    auditRequirement: 'auditLogs/{scopeType}/{scopeId}/items/{auditId}'
  };

  function inferSeverity(reasons){
    var level = 'low';
    unique(reasons).forEach(function(r){
      var reason = reportReasons[r];
      if (!reason) return;
      if ((severityLevels[reason.severity] || severityLevels.low).rank > severityLevels[level].rank) level = reason.severity;
    });
    return level;
  }

  function inferQueue(entityType, reasons, scope){
    var severity = inferSeverity(reasons);
    var reasonSet = unique(reasons);
    if (severity === 'critical' || reasonSet.indexOf('illegalcontent') > -1 || reasonSet.indexOf('underagerisk') > -1 || reasonSet.indexOf('consent') > -1) return 'emergency';
    if (reasonSet.indexOf('adultmisclassification') > -1 || reasonSet.indexOf('selfharm') > -1) return 'adultSafety';
    if (entityType === 'galaxyChat') return 'chat';
    if (scope === 'galaxy') return 'galaxy';
    if (scope === 'universe') return 'universe';
    return 'intake';
  }

  function buildReportContract(input){
    input = input || {};
    var entityType = slug(input.entityType || 'post');
    var reasons = unique(input.reasons || input.reason || 'other');
    var scope = slug(input.scope || 'platform');
    var severity = inferSeverity(reasons);
    var queue = inferQueue(entityType, reasons, scope);
    return {
      version: VERSION,
      reportId: input.reportId || null,
      entityType: entityType,
      entityId: input.entityId || null,
      reporterUid: input.reporterUid || null,
      reasons: reasons,
      severity: severity,
      queue: queue,
      scope: scope,
      state: 'received',
      firebaseReadsEnabled: FIREBASE_READS_ENABLED,
      firebaseWritesEnabled: FIREBASE_WRITES_ENABLED,
      realtimeEnabled: REALTIME_ENABLED,
      enforcementEnabled: ENFORCEMENT_ENABLED,
      auditRequiredBeforeAction: true,
      pathPreview: pathContracts.reports
    };
  }

  function getManifest(){
    return clone({
      version: VERSION,
      owner: OWNER,
      gates: {
        firebaseReadsEnabled: FIREBASE_READS_ENABLED,
        firebaseWritesEnabled: FIREBASE_WRITES_ENABLED,
        realtimeEnabled: REALTIME_ENABLED,
        enforcementEnabled: ENFORCEMENT_ENABLED,
        actionExecutionEnabled: ACTION_EXECUTION_ENABLED
      },
      doctrine: doctrine,
      reportableEntityTypes: reportableEntityTypes,
      reportReasons: reportReasons,
      severityLevels: severityLevels,
      reviewStates: reviewStates,
      queues: queues,
      recommendedActions: recommendedActions,
      pathContracts: pathContracts,
      dependencies: {
        moderationMetadata: !!window.DSDModerationMetadata,
        governanceRegistry: !!window.DSDGovernanceRegistry,
        trustRegistry: !!window.DSDTrustRegistry,
        identityRegistry: !!window.DSDIdentityRegistry,
        firebaseSocialSchema: !!window.DSDFirebaseSocialSchema,
        realtimeOwnershipAudit: !!window.DSDRealtimeOwnershipAudit
      }
    });
  }

  window.DSDModerationRegistry = {
    version: VERSION,
    owner: OWNER,
    gates: {
      firebaseReadsEnabled: FIREBASE_READS_ENABLED,
      firebaseWritesEnabled: FIREBASE_WRITES_ENABLED,
      realtimeEnabled: REALTIME_ENABLED,
      enforcementEnabled: ENFORCEMENT_ENABLED,
      actionExecutionEnabled: ACTION_EXECUTION_ENABLED
    },
    getManifest: getManifest,
    buildReportContract: buildReportContract,
    getReportReasons: function(){ return clone(reportReasons); },
    getReviewStates: function(){ return clone(reviewStates); },
    getQueues: function(){ return clone(queues); },
    getPathContracts: function(){ return clone(pathContracts); },
    inferSeverity: inferSeverity,
    inferQueue: inferQueue
  };

  claim('moderation-registry:doctrine', { doctrine: doctrine, enforcementEnabled: ENFORCEMENT_ENABLED });
  claim('moderation-registry:reportable-entities', { entityTypes: Object.keys(reportableEntityTypes), futureTypes: ['story','universe','galaxy','galaxyStory','galaxyChat','event','mediaAsset'] });
  claim('moderation-registry:report-reasons', { reasons: Object.keys(reportReasons), criticalReasons: ['consent','illegalContent','underageRisk','privacy','violenceThreat','selfHarm'] });
  claim('moderation-registry:review-states', { states: Object.keys(reviewStates), terminalStates: ['actioned','dismissed','appealResolved'] });
  claim('moderation-registry:queues', { queues: Object.keys(queues), emergencyQueue: true, adultSafetyQueue: true });
  claim('moderation-registry:actions', { actions: Object.keys(recommendedActions), auditRequired: true, executionEnabled: ACTION_EXECUTION_ENABLED });
  claim('moderation-registry:path-contracts', { paths: pathContracts, firebaseReadsEnabled: FIREBASE_READS_ENABLED, firebaseWritesEnabled: FIREBASE_WRITES_ENABLED });
  claim('moderation-registry:dependencies', getManifest().dependencies);
  event('MODERATION_REGISTRY_BOOT', { status:'registered', enforcementEnabled: ENFORCEMENT_ENABLED, firebaseWritesEnabled: FIREBASE_WRITES_ENABLED });
})();
